import { useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthHead, Notice } from "@/components/AuthFrame";
import { PasswordFields } from "@/features/auth/PasswordFields";
import { authErrorText, newPasswordProblem } from "@/features/auth/errors";
import { changePassword, meQuery } from "@/lib/auth";

// Wachtwoord wijzigen (H-g02): the current one and a new one twice. Every other session of theirs ends;
// this one goes on.

export const Route = createFileRoute("/_portal/account/password")({
  component: ChangePasswordScreen,
});

function ChangePasswordScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: me } = useSuspenseQuery(meQuery);
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const submit = async () => {
    const problem = newPasswordProblem(t, password, repeat, me.user.email);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      await changePassword(current, password);
      setDone(true);
    } catch (err) {
      setError(authErrorText(t, err, i18n.language));
    } finally {
      setCurrent("");
      setBusy(false);
    }
  };

  return (
    <div className="wl-auth" data-screen-label="Portaal Wachtwoord wijzigen">
      <div className="wl-auth__card">
        <AuthHead title={t("user.password")} />
        {done ? (
          <>
            <Notice tone="success">{t("account.passwordDone")}</Notice>
            <div className="wl-auth__actions">
              <span className="wl-spacer" />
              <Link to="/shipments" className="wl-btn wl-btn--primary wl-btn--lg">
                {t("account.backToPortal")}
              </Link>
            </div>
          </>
        ) : (
          <form
            className="wl-vstack"
            style={{ gap: "var(--space-5)", alignItems: "stretch" }}
            onSubmit={(event) => {
              event.preventDefault();
              void submit();
            }}
          >
            {error && <Notice tone="danger">{error}</Notice>}
            <div className="wl-field">
              <label className="wl-field__label" htmlFor="current-password">
                {t("password.current")}
              </label>
              <input
                id="current-password"
                className="wl-input"
                type="password"
                autoComplete="current-password"
                value={current}
                autoFocus
                onChange={(event) => setCurrent(event.target.value)}
              />
            </div>
            <PasswordFields password={password} repeat={repeat} onPassword={setPassword} onRepeat={setRepeat} email={me.user.email} />
            <p className="wl-auth__foot">{t("account.passwordElsewhere")}</p>
            <div className="wl-auth__actions">
              <button type="button" className="wl-btn wl-btn--ghost" onClick={() => void navigate({ to: "/shipments" })}>
                {t("common.cancel")}
              </button>
              <span className="wl-spacer" />
              <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || current === "" || password === ""}>
                {t("common.save")}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
